import { useMemo, useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio } from '../utils/luminance';

// ─── Slot group definitions ───────────────────────────────────────────────────

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const cta1Def: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const cta2Def: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  const bg = useSlotGroup('pinnedcallouts.bg_image', bgImageDef);
  const cta1 = useSlotGroup('pinnedcallouts.cta1', cta1Def);
  const cta2 = useSlotGroup('pinnedcallouts.cta2', cta2Def);

  // Right-CTA accent picker. The right CTA rests on a white background, so we
  // need a fill that has enough contrast against white. When the property's
  // primary clears WCAG AA against the page background, use it (paired with
  // text-on-primary). Otherwise fall back to the dark `text` token paired with
  // `background` — same logic the PropertyOverview "Explore Floor Plans" CTA
  // uses, so the two CTAs stay visually consistent across themes.
  const tokens = useTokens();
  const [ctaAccent, ctaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  // Parallax: section bounds reference + the element we translate
  const sectionRef = useRef<HTMLElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const parallax = parallaxRef.current;
    if (!section || !parallax) return;

    let rafId = 0;
    let active = false;

    const tick = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      // progress: 0 = section enters from bottom, 0.5 = centred, 1 = exits at top
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      // Background travels 80 px total (±40 px) — slower than page scroll
      const offset = (clamped - 0.5) * 80;
      parallax.style.transform = `translateY(${offset}px)`;
      if (active) rafId = requestAnimationFrame(tick);
    };

    // IntersectionObserver works regardless of whether the scroll container is
    // window, an inner div, or an editor iframe — window.scroll would miss those.
    // rootMargin pre-activates 200 px before the section enters so there is no
    // visible jump when it first scrolls into view.
    const observer = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        if (active) rafId = requestAnimationFrame(tick);
        else cancelAnimationFrame(rafId);
      },
      { threshold: 0, rootMargin: '200px' },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden min-h-[480px] md:min-h-[600px] flex items-center justify-center"
      style={{
        ['--rise-pinned-cta-accent' as string]: ctaAccent,
        ['--rise-pinned-cta-on-accent' as string]: ctaOnAccent,
      }}
    >
      {/* ── Parallax background (z-0) ── */}
      {/*
        parallaxRef is intentionally OUTSIDE EditableSlotGroup so the runtime
        wrapper never becomes its containing block.  The div is 140 % tall
        (top/bottom: -20 %) so the ±40 px rAF translation never exposes bare
        edges; overflow-hidden on the section keeps everything contained.
      */}
      <div
        ref={parallaxRef}
        className="absolute left-0 right-0 z-0"
        style={{ top: '-20%', bottom: '-20%', willChange: 'transform' }}
      >
        {/* Inner absolute div is the positioning context for VibesitesImage */}
        <div className="absolute inset-0">
          <EditableSlotGroup groupPath="pinnedcallouts.bg_image" definition={bgImageDef}>
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt={bg.alt || ''}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      {/* ── Dark overlay (z-[1], above bg, below content) ── */}
      <div className="absolute inset-0 bg-black/55 z-[1]" />

      {/* ── Content (z-10) ── */}
      <div
        ref={contentRef}
        className="reveal-fade-up relative z-10 text-center max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32"
      >
        {/* Subtitle */}
        <RichTextContent
          path="pinnedcallouts.subtitle"
          label="Subtitle"
          defaultValue="<p>Experience The Difference</p>"
          as="p"
          className="font-body text-base uppercase tracking-[0.3em] text-white/80 mb-4"
        />

        {/* Divider */}
        <div className="w-10 h-px bg-white/60 mx-auto mb-6" />

        {/* Title */}
        <RichTextContent
          path="pinnedcallouts.title"
          label="Title"
          defaultValue="<p>Live Where You Love</p>"
          as="h2"
          className="pinnedcallouts-title-glow font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] uppercase tracking-wide mb-10"
        />

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <EditableSlotGroup groupPath="pinnedcallouts.cta1" definition={cta1Def}>
            <a
              href={cta1.url || '/schedule-a-tour'}
              target={cta1._target || '_self'}
              className="pinnedcallouts-cta pinnedcallouts-cta--light relative inline-flex items-center gap-2.5 px-10 py-4 font-body text-sm font-medium tracking-[0.2em] uppercase overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta1.label || 'Schedule a Tour'}</span>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="pinnedcallouts.cta2" definition={cta2Def}>
            <a
              href={cta2.url || '/floor-plans'}
              target={cta2._target || '_self'}
              className="pinnedcallouts-cta pinnedcallouts-cta--dark relative inline-flex items-center gap-2.5 px-10 py-4 font-body text-sm font-medium tracking-[0.2em] uppercase overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta2.label || 'View Floor Plans'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pinnedcallouts-title-glow {
          text-shadow:
            0 0 12px rgba(255, 255, 255, 0.35),
            0 0 30px rgba(255, 255, 255, 0.18);
        }
        /* Shared CTA base — mirrors .hero-cta */
        .pinnedcallouts-cta {
          border-radius: 2px;
          box-shadow: none;
        }
        .pinnedcallouts-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          transition: background 0.3s ease;
        }
        .pinnedcallouts-cta:hover {
          box-shadow: none;
          transform: translateY(-2px);
        }
        .pinnedcallouts-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        /* Light variant — transparent bg, white border/text (matches hero CTA) */
        .pinnedcallouts-cta--light {
          background: transparent;
          border: 1.5px solid rgba(255, 255, 255, 0.75);
          color: #fff;
        }
        .pinnedcallouts-cta--light::before {
          background: rgba(255, 255, 255, 0);
        }
        .pinnedcallouts-cta--light:hover {
          background: transparent;
          border-color: #fff;
        }
        .pinnedcallouts-cta--light:hover::before {
          background: rgba(255, 255, 255, 0.12);
        }
        /* Dark (inverted) variant — white bg / dark border + label at rest
           (the contrast-aware accent picker keeps the resting style legible
           against the page background). On hover, the button fills with the
           theme primary and flips the label to text-on-primary regardless of
           the picker, so the hover state always reflects the brand color. */
        .pinnedcallouts-cta--dark {
          background: var(--vibe-color-background, #fff);
          border: 1.5px solid var(--rise-pinned-cta-accent, var(--vibe-color-primary));
          color: var(--rise-pinned-cta-accent, var(--vibe-color-primary));
        }
        .pinnedcallouts-cta--dark::before {
          background: var(--vibe-color-primary);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .pinnedcallouts-cta--dark:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-primary);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .pinnedcallouts-cta--dark:hover::before {
          opacity: 1;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Experience The Difference</p>',
    },
    'pinnedcallouts.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Live Where You Love</p>',
    },
  },
  slotGroups: {
    'pinnedcallouts.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 2,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'pinnedcallouts.cta1': {
      label: 'Primary CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'pinnedcallouts.cta2': {
      label: 'Secondary CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
